import { useMemo, useState } from 'react';
import { useMemoizedFn, useRequest } from 'ahooks';

const useTeaPagination = (service, options = {}) => {
  const {
    clientPaging = false, // 是否前端分页，默认false，请求后端
    defaultPageSize = 10,
    debounceWait = 0,
    totalName = 'total',
    defaultParams,
    formParams,
    sortsRef,
    onSortsRequest,
    ...rest
  } = options;

  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageIndex, setPageIndex] = useState(1);

  let result = {};
  if (clientPaging) {
    result = useRequest(service, {
      defaultParams: [{ ...defaultParams }],
      refreshDepsAction: () => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        changePageIndex(1);
      },
      debounceWait,
      ...rest,
    });
  } else {
    result = useRequest(service, {
      defaultParams: [
        { ...defaultParams, pageIndex: 1, pageSize: defaultPageSize },
      ],
      refreshDepsAction: () => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        changePageIndex(1);
      },
      debounceWait,
      ...rest,
    });
  }

  let pageIndexNow = pageIndex;
  let pageSizeNow = pageSize;
  if (!clientPaging) {
    const { pi = 1, ps = defaultPageSize } = result.params[0] || {};
    pageIndexNow = pi;
    pageSizeNow = ps;
  }

  let recordCount = 0;
  if (result.data) {
    recordCount = result.data[totalName];
  }
  const totalPage = useMemo(
    () => Math.ceil(recordCount / pageSizeNow),
    [pageSizeNow, recordCount],
  );

  const onPagingChange = (pagingQuery) => {
    if (!pagingQuery) return;
    const { pageIndex: pi, pageSize: ps } = pagingQuery;
    let toPageIndex = pageIndexNow;
    if (pi !== null && pi !== undefined) {
      toPageIndex = pi <= 0 ? 1 : pi;
    } else {
      toPageIndex = 1;
    }
    let toPageSize = pageSizeNow;
    if (ps !== null && ps !== undefined) {
      toPageSize = ps <= 0 ? 1 : ps;
    }
    const tempTotalPage = Math.ceil(recordCount / toPageSize);
    if (toPageIndex > tempTotalPage) {
      toPageIndex = Math.max(1, tempTotalPage);
    }

    // 如果是客户端分页，则不需要发请求
    if (clientPaging) {
      if (toPageIndex !== pageIndexNow) {
        setPageIndex(toPageIndex);
      }
      if (toPageSize !== pageSizeNow) {
        setPageSize(toPageSize);
      }
    } else {
      const [oldPaginationParams = {}, ...restParams] = result.params || [];
      let sortParams = { sorter: sortsRef.current };
      if (typeof onSortsRequest === 'function') {
        sortParams = onSortsRequest(sortsRef.current);
      }

      if (!formParams) {
        result.run(
          {
            ...defaultParams,
            ...oldPaginationParams,
            pageIndex: toPageIndex,
            pageSize: toPageSize,
            ...sortParams,
          },
          ...restParams,
        );
      } else {
        setTimeout(() => {
          const { form } = formParams;
          form
            .submit()
            ?.then((values = {}) => {
              result.run(
                {
                  ...defaultParams,
                  ...oldPaginationParams,
                  pageIndex: toPageIndex,
                  pageSize: toPageSize,
                  ...sortParams,
                  ...values,
                },
                ...restParams,
              );
            })
            .catch((err) => err);
        });
      }
    }
  };

  const changePageIndex = (pi) => {
    onPagingChange({ pageIndex: pi, pageSize: pageSizeNow });
  };

  const changePageSize = (ps) => {
    onPagingChange({ pageIndex: pageIndexNow, pageSize: ps });
  };

  return {
    ...result,
    pagination: {
      pageIndex: pageIndexNow,
      pageSize: pageSizeNow,
      recordCount,
      totalPage,
      onPagingChange: useMemoizedFn(onPagingChange),
      changePageIndex: useMemoizedFn(changePageIndex),
      changePageSize: useMemoizedFn(changePageSize),
    },
  };
};

export default useTeaPagination;
